import { useCallback, useEffect, useState } from 'react';
import { MenuItem, Select, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Info from '@mui/icons-material/Info';
import { range, find } from 'lodash-es';
import { GridActionsCellItem, Table } from '../../../components/table/Table';
import { TableStatus } from '../../../components/table/TableStatus';
import {
  getFarmqaOperations,
  syncFarmqaOperations,
} from '../../../services/admin-service';
import { useAlerts } from '../../../hooks/use-alerts';
import { Operation } from './Operation';

const years = range(2024, new Date().getFullYear() + 1);
export function Operations() {
  const [operations, setOperations] = useState();
  const [operation, setOperation] = useState();
  const [selected, setSelected] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [isSyncing, setSyncing] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const { showError, showInfo } = useAlerts();

  const loadOperations = useCallback(() => {
    setLoading(true);
    getFarmqaOperations()
      .then(setOperations)
      .catch(showError)
      .finally(() => setLoading(false));
  }, [showError]);

  useEffect(() => {
    loadOperations();
  }, [loadOperations]);

  function buildOperationDetails() {
    if (!operation) return null;

    return (
      <Operation
        year={year}
        operation={operation}
        onClose={() => setOperation(null)}
      />
    );
  }

  function buildYearSelector() {
    return (
      <Select
        value={year}
        onChange={(event) => setYear(Number(event.target.value))}
        size="small"
        variant="outlined"
      >
        {years.map((y, i) => (
          <MenuItem key={i} value={y}>
            {y}
          </MenuItem>
        ))}
      </Select>
    );
  }

  return (
    <>
      {buildOperationDetails()}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: 2,
          pt: 0,
          height: '100%',
        }}
      >
        <Table
          loading={isLoading}
          columns={buildColumns()}
          rows={operations || []}
          checkboxSelection
          rowSelectionModel={selected}
          onRowSelectionModelChange={setSelected}
          hideFooter={false}
          initialState={{
            sorting: {
              sortModel: [
                { field: 'organization' },
                { field: 'region' },
                { field: 'primaryAgronomist' },
                { field: 'name' },
              ],
            },
          }}
          preActions={buildYearSelector()}
          actions={[
            <LoadingButton
              key="sync"
              onClick={syncSelected}
              disabled={!selected.length}
              loading={isSyncing}
              keep-open="true"
            >
              Sync
            </LoadingButton>,
          ]}
        />
      </Box>
    </>
  );

  function buildColumns() {
    return [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
      },
      {
        field: 'organization',
        headerName: 'Organization',
        flex: 1,
      },
      {
        field: 'region',
        headerName: 'Region',
        flex: 0.75,
      },
      {
        field: 'primaryAgronomist',
        headerName: 'PA',
        flex: 1,
      },
      {
        field: 'contract',
        headerName: 'Contract',
        flex: 1,
      },
      {
        field: 'syncStatus',
        headerName: 'Status',
        flex: 0.75,
        renderCell: ({ row }) => <TableStatus status={row.syncStatus} />,
      },
      {
        field: 'syncedAt',
        headerName: 'Synced At',
        flex: 1,
        type: 'dateTime',
        valueFormatter: ({ value }) =>
          value ? new Date(value).toLocaleString() : '--',
      },
      {
        field: 'fieldCount',
        headerName: 'Fields',
        flex: 0.75,
        type: 'number',
        valueGetter: ({ row }) => row.counts.fields?.count || 0,
      },
      {
        field: 'syncedFieldCount',
        headerName: 'Synced',
        flex: 0.75,
        type: 'number',
        valueGetter: ({ row }) =>
          find(row.counts.syncedFields, { year })?.count || 0,
      },
      {
        field: 'unsyncedFieldCount',
        headerName: 'Unsynced',
        flex: 0.75,
        type: 'number',
        valueGetter: ({ row }) => {
          const certified = row.counts.fields?.count || 0;
          const synced = find(row.counts.syncedFields, { year })?.count || 0;
          return certified - synced;
        },
      },
      {
        field: 'syncedLayerCount',
        headerName: 'Layers',
        flex: 0.75,
        type: 'number',
        valueGetter: ({ row }) =>
          find(row.counts.syncedLayers, { year })?.count || 0,
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        getActions: ({ row }) => [
          <GridActionsCellItem
            icon={<Info />}
            label="Details"
            disabled={!row.syncedAt}
            onClick={() => setOperation(row)}
          />,
        ],
      },
    ];
  }

  function syncSelected() {
    setSyncing(true);
    syncFarmqaOperations(selected, year)
      .then(() => {
        loadOperations();
        showInfo('Sync schedule');
      })
      .catch(showError)
      .finally(() => setSyncing(false));
  }
}
